import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import type { PopDisplays_ChannelFrame } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { displaysIcon, downloadIcon } from 'c-main/icons';
import type { FilterableEntityTableRefAPI } from 'c-pagination';
import {
    FilterableEntityTable,
    FilterableEntityTableRendererType,
    generateColumn,
} from 'c-pagination';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { useAPIClientRequest } from 'c-hooks';
import apiClient from 'c-data/apiClient';
import type { AlertProps } from '@mui/material';
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import to from 'await-to-js';
import { usePaginatedEntityData } from 'c-data';
import { Alert, Button, DialogV2, TextAreaField } from 'c-components';
import ReactGA from 'react-ga';
import { useCommonTranslation } from 'c-translation';
import EditIcon from '@mui/icons-material/Edit';
import { generatePath, useHistory } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import { NetworkRequestState } from 'c-data-layer';
import ChannelFrameImportButton from './ChannelFrameImportButton';

const paginationTag = 'pop-display-list-search';
const textSearchCols = [
    'id',
    'channel',
    'source',
    'displays.id',
    'displays.displays_portal_id',
    'frame',
    'player',
];

const columns = setId => [
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.idCol',
        field: 'id',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.sourceCol',
        field: 'source',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.channelCol',
        field: 'channel',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.frameCol',
        field: 'frame',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.frameExtIdCol',
        field: 'frame_extid',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.playerCol',
        field: 'player',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.playerExtIdCol',
        field: 'player_extid',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.uuidCol',
        field: 'uuid',
        entityName: 'PopDisplays_ChannelFrame',
        orderable: true,
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.displayPortalIdCol',
        field: 'displays_portal_id',
        entityName: 'PopDisplays_ChannelFrame',
        RenderCell: ({ entity }) => (
            <Stack direction="row" alignItems="center">
                {Array.isArray(entity.displays_portal_id)
                    ? entity.displays_portal_id.join(', ')
                    : ''}
                <IconButton onClick={() => setId(entity.id)}>
                    <AddIcon />
                </IconButton>
            </Stack>
        ),
    }),
    generateColumn<PopDisplays_ChannelFrame>({
        headerTitle: 'Modules.PopDisplays.ChannelFrame.table.actions',
        field: '',
        entityName: 'PopDisplays_ChannelFrame',
        RenderCell: ({ row }) => {
            const history = useHistory();
            const id = row.id;
            const route = generatePath(PostAuthRoutes.PopDisplays.ChannelFrameOverview, {
                id,
            });
            return (
                <IconButton
                    onClick={e => {
                        e.stopPropagation();
                        history.push(route);
                    }}
                >
                    <EditIcon />
                </IconButton>
            );
        },
    }),
];

const ChannelFrameListPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const [id, setId] = useState<number | string | null>(null);
    const { start, data, error, isLoading, reset, requestState } = useAPIClientRequest(
        apiClient.PopDisplays.addDisplayPortalId,
    );
    const [inputValue, setInputValue] = useState('');

    const parsedFrameIds = useMemo(
        () =>
            inputValue
                .split('\n')
                .map(id => Number(id.trim()))
                .filter(Boolean),
        [inputValue],
    );
    const handleAddDisplayPortalId = useCallback(async () => {
        const [, success] = await to(start(id, parsedFrameIds));
    }, [id, parsedFrameIds, start]);
    const ref = useRef<FilterableEntityTableRefAPI>();
    const onClose = useCallback(() => {
        ref?.current?.search();
    }, []);
    const t = useCommonTranslation();
    const handlecloseDialog = useCallback(() => {
        setId(null);
        setInputValue('');
        reset();
    }, [reset]);
    useEffect(() => {
        if (requestState === NetworkRequestState.Success) {
            ref.current?.search(1);
            setInputValue('');
        }
    }, [requestState]);
    return (
        <>
            <DialogV2
                onClose={handlecloseDialog}
                open={id !== null}
                title="Modules.PopDisplays.ChannelFrame.addDisplayPortalIds"
            >
                <Stack>
                    <TextAreaField
                        label={t('Modules.PopDisplays.ChannelFrame.ids')}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        placeholder="Enter one ID per line"
                        showCharacterCount={false}
                        size="small"
                        variant="outlined"
                    />
                    <Typography>
                        {t('Modules.PopDisplays.ChannelFrame.ids')}: {parsedFrameIds.length}
                    </Typography>
                </Stack>
                <Button onClick={handleAddDisplayPortalId}>
                    {isLoading ? (
                        <CircularProgress />
                    ) : (
                        t('Modules.PopDisplays.ChannelFrame.submit')
                    )}
                </Button>
                {data && <Alert severity="success">{String(data?.data?.message)}</Alert>}
                {error && (
                    <Alert severity="error" onClose={reset}>
                        {String(error)}
                    </Alert>
                )}
            </DialogV2>
            <FullPageEntityTableWrapper
                title="Modules.PopDisplays.ChannelFrame.metaTitle"
                controls={useMemo(
                    () => [
                        <ChannelFrameImportButton onClose={onClose} key="import" />,
                        <DownloadBtn key="download" />,
                    ],
                    [onClose],
                )}
            >
                <FilterableEntityTable
                    ref={ref}
                    key={paginationTag}
                    tag={paginationTag}
                    columns={columns(setId)}
                    baseEntityName="PopDisplays_ChannelFrame"
                    textSearchColumns={textSearchCols}
                    rendererType={FilterableEntityTableRendererType.Contained}
                    revertToIdSearchOnNumberOnlyInput={false}
                    orderBy="id"
                    direction="desc"
                    showFilters
                    filtersInlineSearch
                />
            </FullPageEntityTableWrapper>
        </>
    );
};

const DownloadBtn: React.FC = () => {
    const { paginatedData } = usePaginatedEntityData(paginationTag, 'PopDisplays_ChannelFrame');
    const { start, isLoading, hasSucceeded, hasFailed, error, reset } = useAPIClientRequest(
        apiClient.PopDisplays.channelFrameExport,
    );
    const t = useCommonTranslation();
    const filterLength = useMemo(
        () => paginatedData?.filters['filter.source']?.length ?? 0,
        [paginatedData?.filters],
    );
    const download = useCallback(async () => {
        const [err, response] = await to(
            start({
                filters: paginatedData?.filters ?? {},
                page: 1,
                perPage: 9999999,
            }),
        );
        if (!err) {
            const blob = new Blob([response?.data], { type: 'text/csv;charset=utf-8;' });

            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.href = url;
            link.setAttribute('download', 'channel-frames.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }, [start, paginatedData]);

    const alertColor = useMemo<AlertProps['color']>(() => {
        if (hasSucceeded) return 'success';
        if (hasFailed) return 'error';

        return 'info';
    }, [hasSucceeded, hasFailed]);

    const message = useMemo(() => {
        if (hasFailed) return String(error);
        if (hasSucceeded) return t('Modules.PopDisplays.ChannelFrame.exportSuccess');

        return null;
    }, [error, hasFailed, hasSucceeded, t]);

    return (
        <>
            <DialogV2 onClose={reset} open={hasSucceeded || hasFailed}>
                <Alert variant="outlined" color={alertColor}>
                    {message}
                </Alert>
            </DialogV2>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <IconButton onClick={download} disabled={filterLength === 0}>
                    {downloadIcon}
                </IconButton>
            )}
        </>
    );
};

const setup: PageConfig = {
    id: 'ChannelFrameListPage',
    component: ChannelFrameListPage,
    disableDesktopContentScrolling: true,
    systemSearch: {
        title: 'Modules.PopDisplays.ChannelFrame.metaTitle',
        description: 'Modules.PopDisplays.ChannelFrame.description',
        icon: displaysIcon,
        type: 'page',
        aliases: 'SystemSearch.aliases.popDisplaysChannelFrameList',
        route: PostAuthRoutes.PopDisplays.ChannelFrameList,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.PopDisplays.AdminHome.metaTitle',
                link: PostAuthRoutes.PopDisplays.PopDisplaysHome,
            },
            { label: 'Modules.PopDisplays.ChannelFrame.metaTitle' },
        ],
        permissions: [
            PermissionName.DisplaysDisplay_channel_frameRead,
            PermissionName.DisplaysDisplay_channel_frameUpdate,
            PermissionName.DisplaysDisplay_channel_frameCreate,
        ],
    },
};

export default setup;
